import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ControleEstoqueService } from '@shared/services/apis/produto/controle-estoque.service';
import { MovimentacaoEstoqueDetalheStruct } from '@shared/services/structs/produto/movimentacao-estoque-detalhe.struct';

export interface MovimentacoesEstoqueDialogData {
  titulo: string;
  idsEstoqueMovimentacao: number[];
}

/**
 * Lista somente leitura das movimentações de estoque de um lançamento (NF ou pedido), a partir dos ids guardados na
 * situação de estoque. O nome do produto abre o detalhe do controle de estoque em nova aba.
 *
 * ```ts
 * this.dialog.open(MovimentacoesEstoqueDialogComponent, {
 *   width: '900px', maxWidth: '96vw', autoFocus: false,
 *   data: { titulo: 'Movimentações de estoque · NF-e 123', idsEstoqueMovimentacao },
 * });
 * ```
 */
@Component({
  selector: 'app-movimentacoes-estoque-dialog',
  standalone: true,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    AbntPipe,
    EmptyStateComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './movimentacoes-estoque-dialog.component.html',
  styleUrl: './movimentacoes-estoque-dialog.component.css',
})
export class MovimentacoesEstoqueDialogComponent implements OnInit, OnDestroy {
  carregando = true;
  movimentacoes: MovimentacaoEstoqueDetalheStruct[] = [];
  /** Coluna de custo só aparece quando alguma movimentação tem custo. */
  temCusto = false;
  colunas: string[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<MovimentacoesEstoqueDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MovimentacoesEstoqueDialogData,
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.controleEstoqueService.movimentacoesDetalhe(this.data.idsEstoqueMovimentacao)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resposta => {
          this.carregando = false;
          if (resposta.isError) {
            this.alertService.show('Erro', resposta.errorDescription || 'Não foi possível carregar as movimentações.', AlertType.error);
            return;
          }
          this.movimentacoes = resposta.lista ?? [];
          this.temCusto = this.movimentacoes.some(m => m.custoUnitario != null);
          this.colunas = ['data', 'produto', 'localizacao', 'operacao', 'quantidade', ...(this.temCusto ? ['custo'] : []), 'documento'];
        },
        error: () => {
          this.carregando = false;
          this.alertService.show('Erro', 'Não foi possível carregar as movimentações.', AlertType.error);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  abrirProduto(idProduto: number): void {
    window.open(`${window.location.origin}/suprimentos/controle-estoque-detalhe/${idProduto}`, '_blank', 'noopener');
  }

  fechar(): void {
    this.dialogRef.close();
  }
}
